import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { HeroTone } from '@/features/events';
import { getRouteSeo } from '@/features/seo';
import { SiteHeader } from '@/components/navigation/SiteHeader';
import { SiteFooter } from '@/components/navigation/SiteFooter';
import { cx } from '@/lib/cx';
import styles from './Page.module.css';

export interface PageProps {
  children: ReactNode;
  /** Titulo del documento. Se le anade el sufijo de marca. */
  documentTitle: string;
  /** Meta description. Obligatoria: cada pagina la suya. */
  description: string;
  /** Acento cromatico del destino (styles/tones.css). El hub no lleva. */
  tone?: HeroTone;
  className?: string;
}

const TITLE_SUFFIX = 'Gira 2027 · Nueva Humanidad Galáctica';

/**
 * Envoltorio de PAGINA: cabecera, contenido y pie. Toda pagina del sitio se
 * monta dentro de este componente — asi la navegacion y el pie son identicos
 * en las seis y ninguna FORGE los duplica.
 */
export function Page({ children, documentTitle, description, tone, className }: PageProps) {
  const { pathname } = useLocation();

  /* ---------------------------------------------------------------------------
   * EL `<head>` LO ESCRIBE UN SOLO SITIO, Y ES ESTE.
   *
   * El `<head>` que ven WhatsApp, Instagram y X NO sale de aqui: lo escribe el
   * BUILD (`scripts/seo/plugin.ts`) en el HTML servido, porque esos rastreadores
   * no ejecutan JavaScript. Este efecto solo sirve al usuario que YA esta dentro
   * y navega entre paginas sin recargar.
   *
   * Justo por eso lee la MISMA ficha (`features/seo`) que uso el build: si aqui
   * se rotulara otra cosa, la pestana del navegador y la vista previa compartida
   * dirian titulos distintos de la misma pagina, y Google — que si ejecuta JS —
   * veria uno diferente del que sirvio el servidor.
   *
   * `documentTitle`/`description` siguen siendo el contrato de pagina y son el
   * respaldo para lo que NO es una de las seis rutas (la 404, y cualquier ruta
   * futura que todavia no tenga ficha).
   * ------------------------------------------------------------------------ */
  useEffect(() => {
    const seo = getRouteSeo(pathname);
    document.title = seo?.title ?? `${documentTitle} — ${TITLE_SUFFIX}`;

    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (meta) meta.content = seo?.description ?? description;

    // La canonica del HTML servido es la de la ruta por la que se ENTRO. Al
    // navegar dentro hay que moverla, o `/cancun` seguiria declarandose `/`.
    if (seo) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = seo.canonical;
    }
  }, [pathname, documentTitle, description]);

  return (
    <div className={cx(styles.page, className)} data-tone={tone}>
      <a className="az-skip-link" href="#contenido">
        Saltar al contenido
      </a>
      <SiteHeader />
      <main id="contenido" className={styles.main}>
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}
